import { lazy, Suspense } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import Loader from '../components/common/Loader';
import LoadingBar from '../components/common/LoadingBar';
import { AdminRoute, ProtectedRoute } from './ProtectedRoute';

// Public pages - Load immediately (small bundle)
import LandingPage from '../pages/public/LandingPage';
import LoginPage from '../pages/public/LoginPage';
import SignUpPage from '../pages/public/SignUpPage';

// Lazy load other pages - Load only when needed
const AboutPage = lazy(() => import('../pages/public/AboutPage'));
const DepartmentsPage = lazy(() => import('../pages/public/DepartmentsPage'));


// Student pages - Lazy loaded
const Dashboard = lazy(() => import('../pages/student/Dashboard'));
const BrowsePapers = lazy(() => import('../pages/student/BrowsePapers'));
const PaperDetailsPage = lazy(() => import('../pages/student/PaperDetailsPage'));
const UploadPaper = lazy(() => import('../pages/student/UploadPaper'));
const MyUploads = lazy(() => import('../pages/student/MyUploads'));
const ProfilePage = lazy(() => import('../pages/student/ProfilePage'));
const SettingsPage = lazy(() => import('../pages/student/SettingsPage'));
const BackpackPage = lazy(() => import('../pages/student/BackpackPage'));
const FeedbackPage = lazy(() => import('../pages/student/FeedbackPage'));

// Admin pages - Lazy loaded (biggest savings!)
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const PapersPage = lazy(() => import('../pages/admin/PapersPage'));
const UsersPage = lazy(() => import('../pages/admin/UsersPage'));
const SystemLogsPage = lazy(() => import('../pages/admin/SystemLogsPage'));
const ReportsPage = lazy(() => import('../pages/admin/ReportsPage'));
const ReportDetailsPage = lazy(() => import('../pages/admin/ReportDetailsPage'));
const NotificationsPage = lazy(() => import('../pages/admin/NotificationsPage'));
const PendingApprovals = lazy(() => import('../pages/admin/PendingApprovals'));
const AdminSettingsPage = lazy(() => import('../pages/admin/SettingsPage'));

// Loading fallback component
const PageLoader = () => (
    <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
        <Loader />
    </div>
);

const AppRoutes = () => {
    return (
        <Router>
            <LoadingBar />
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignUpPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/departments" element={<DepartmentsPage />} />


                    {/* Protected Student Routes */}
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/browse" element={
                        <ProtectedRoute>
                            <BrowsePapers />
                        </ProtectedRoute>
                    } />
                    <Route path="/paper/:id" element={
                        <ProtectedRoute>
                            <PaperDetailsPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/upload" element={
                        <ProtectedRoute>
                            <UploadPaper />
                        </ProtectedRoute>
                    } />
                    <Route path="/my-uploads" element={
                        <ProtectedRoute>
                            <MyUploads />
                        </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                        <ProtectedRoute>
                            <ProfilePage />
                        </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                        <ProtectedRoute>
                            <SettingsPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/backpack" element={
                        <ProtectedRoute>
                            <BackpackPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/feedback" element={
                        <ProtectedRoute>
                            <FeedbackPage />
                        </ProtectedRoute>
                    } />

                    {/* Admin Routes */}
                    <Route path="/admin/dashboard" element={
                        <AdminRoute>
                            <AdminDashboard />
                        </AdminRoute>
                    } />
                    <Route path="/admin/papers" element={
                        <AdminRoute>
                            <PapersPage />
                        </AdminRoute>
                    } />
                    <Route path="/admin/users" element={
                        <AdminRoute>
                            <UsersPage />
                        </AdminRoute>
                    } />
                    <Route path="/admin/logs" element={
                        <AdminRoute>
                            <SystemLogsPage />
                        </AdminRoute>
                    } />
                    <Route path="/admin/reports" element={
                        <AdminRoute>
                            <ReportsPage />
                        </AdminRoute>
                    } />
                    <Route path="/admin/reports/:id" element={
                        <AdminRoute>
                            <ReportDetailsPage />
                        </AdminRoute>
                    } />
                    <Route path="/admin/notifications" element={
                        <AdminRoute>
                            <NotificationsPage />
                        </AdminRoute>
                    } />
                    <Route path="/admin/pending" element={
                        <AdminRoute>
                            <PendingApprovals />
                        </AdminRoute>
                    } />
                    <Route path="/admin/settings" element={
                        <AdminRoute>
                            <AdminSettingsPage />
                        </AdminRoute>
                    } />
                </Routes>
            </Suspense>
        </Router>
    );
};

export default AppRoutes;
